import Image from "next/image";
import { memo, useRef, useEffect, useState } from "react";
import Link from "next/link";
import ClipLoader from "react-spinners/ClipLoader";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { getCloudfrontUrl } from "../utils/mediaHelper";
import { fetchBlogData } from "./http/commonApi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const BlogSlider = memo(({ className = "", initialBlogs = [] }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const lang = router.locale || "en";

  // ✅ support both array and {blogs: []}
  const normalizedInitialBlogs = Array.isArray(initialBlogs)
    ? initialBlogs
    : (initialBlogs?.blogs ?? []);

  const [blogs, setBlogs] = useState(normalizedInitialBlogs);
  const [loading, setLoading] = useState(!normalizedInitialBlogs?.length);

  const page = 1;
  const limit = 6;

  // ✅ only fetch if SSR didn’t provide blogs
  useEffect(() => {
    if (normalizedInitialBlogs?.length) return;

    const fetchBlogsListData = async () => {
      try {
        setLoading(true);
        const { data } = await fetchBlogData({ page, limit, lang });
        setBlogs(data?.blogs ?? []);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogsListData();
  }, [lang, normalizedInitialBlogs?.length]);

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [navigation, setNavigation] = useState({ prevEl: null, nextEl: null });

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      setNavigation({ prevEl: prevRef.current, nextEl: nextRef.current });
    }
  }, []);

  return (
    <section
      className={`w-full flex flex-col items-center justify-start box-border text-text-primary px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] pb-[40px] md:pb-[40px] lg:pb-[60px] xl:pb-[60px] max-w-[1600px] mx-auto ${className}`}
    >
      <div className="flex flex-col items-start w-full gap-6 md:gap-10">
        {/* Heading Row */}
        <div className="flex flex-col items-start justify-between w-full gap-6 sm:flex-row sm:items-center">
          <h2 className="text-[28px] sm:text-[32px] lg:text-[40px] font-medium leading-[120%] m-0">
            {t("blogs.title")}
          </h2>

          {lang === "ar" ? (
            <div className="flex gap-3">
              <button
                ref={nextRef}
                className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
                aria-label="Next"
              >
                <IoIosArrowForward size={20} />
              </button>
              <button
                ref={prevRef}
                className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
                aria-label="Previous"
              >
                <IoIosArrowBack size={20} />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                ref={prevRef}
                className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
                aria-label="Previous"
              >
                <IoIosArrowBack size={20} />
              </button>
              <button
                ref={nextRef}
                className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
                aria-label="Next"
              >
                <IoIosArrowForward size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center w-full py-20">
            <ClipLoader color="#EF4444" size={48} />
          </div>
        ) : (
          <div className="w-full overflow-visible px-2 -mx-2 py-4 -my-4">
            <Swiper
              dir={lang === "ar" ? "ltr" : undefined}
              modules={[Navigation]}
              spaceBetween={24}
              navigation={navigation}
              onBeforeInit={(swiper) => {
                if (typeof swiper.params.navigation !== "boolean") {
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                }
              }}
              breakpoints={{
                320: { slidesPerView: 1 },
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 2 },
                1280: { slidesPerView: 4 },
              }}
              className="w-full"
              style={{ paddingBottom: "0.5rem" }}
            >
              {Array.isArray(blogs) &&
                blogs.map((blog, index) => {
                  const href = `/blog/${blog?.slug}`;
                  const src = getCloudfrontUrl(
                    blog?.coverImage?.url || "/blog-posts@3x.png",
                  );

                  return (
                    <SwiperSlide key={blog?._id || blog?.slug || index}>
                      <Link
                        href={href}
                        className="block no-underline text-inherit"
                      >
                        <div className="bg-white rounded-[20px] overflow-hidden shadow-md cursor-pointer flex flex-col transition-transform hover:scale-[1.02]">
                          <div className="relative w-full aspect-[1200/630] overflow-hidden rounded-t-[20px] bg-gray-50">
                            <Image
                              src={src}
                              alt={
                                blog?.coverImage?.alt ||
                                `Blog ${index + 1} Image`
                              }
                              fill
                              className="object-contain object-center"
                              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                              // priority={index < 2}
                            />
                          </div>

                          <div className="flex flex-col gap-2 p-4">
                            <h3 className="text-[15px] font-semibold leading-[140%] text-text-primary m-0">
                              {blog?.title}
                            </h3>
                          </div>
                        </div>
                      </Link>
                    </SwiperSlide>
                  );
                })}
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
});

BlogSlider.propTypes = {
  className: PropTypes.string,
  initialBlogs: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default BlogSlider;
